/**
 * InventarioApiClient -- helpers especificos para verificacion de inventario.
 *
 * Encapsula consultas al endpoint generico /api/sync/verificacion_inventario para
 * extraer historial por usuario, ultimo conteo, etc. La sincronizacion hacia el
 * Desktop V1.5 (POST a endpoint dedicado del Desktop) tambien vive aqui cuando
 * Argy-D T5 publique su endpoint.
 *
 * Mision: PANEL-CONTEO-INTELIGENTE-V1-001 T4 + T6
 *
 * @module infrastructure/api/InventarioApiClient
 */

import { ApiBase } from './ApiBase.js';
import { negocioDeLaSubida } from '../../application/sync/negocioDeLaSubida.js';

const BASE_SYNC = '/api/sync/verificacion_inventario';
const BASE_BIZ_INSUMOS = '/api/v1/businesses'; // /:bizId/insumos | /:bizId/insumos/:id/config

/**
 * Cache de respuestas getInsumosWithConfig por businessId con TTL.
 * Invalidado por: patchInsumoConfig (same biz) o BroadcastChannel cross-tab.
 */
const _insumosCache = new Map(); // bizId -> { ts, items }
const INSUMOS_CACHE_TTL_MS = 60_000;

export function invalidateInsumosCache(bizId) {
  if (!bizId) { _insumosCache.clear(); return; }
  _insumosCache.delete(bizId);
}

export const InventarioApiClient = {
  /**
   * GET /api/v1/businesses/{bizId}/insumos
   * Retorna lista de insumos del negocio CON config sub_estados_config + unidad_conteo + cantidad_por_unidad.
   * Mision: PANEL-INVENTARIO-CONFIG-PERSIST-BD-V1-001 T3 (Argy-A T1 backend).
   *
   * Cache TTL 60s por businessId. Pasa { fresh: true } para bypass cache.
   *
   * Response esperado:
   * { success: true, data: [{ id, nombre, sub_estados_config: {enabled, estados}|null,
   *                            unidad_conteo, cantidad_por_unidad, Inventario, Costo, Grupo, ... }] }
   *
   * Fallback: si endpoint NO existe (404 mientras Argy-A no ha publicado T1), retorna
   * { ok: false, fallback: true } y el caller usa la fuente legacy (/api/sync/insumos).
   */
  async getInsumosWithConfig(businessId, opts = {}) {
    if (!businessId) return { ok: true, data: [] };
    if (!opts.fresh) {
      const hit = _insumosCache.get(businessId);
      if (hit && (Date.now() - hit.ts) < INSUMOS_CACHE_TTL_MS) {
        return { ok: true, data: hit.items, totalCount: hit.totalCount ?? hit.items.length, cached: true };
      }
    }
    // V2 ROUND 2 (bit #1308) - root cause fix:
    // Endpoint retorna ApiResponse<InsumosListResponseDto> = { success, data: { items, totalCount, page, pageSize } }
    // NO un array directo. Mi codigo V1 leia `res.data.data` esperando array y daba [] vacio.
    // pageSize maximo permitido por endpoint = 100.
    const pageSize = Math.min(opts.pageSize || 100, 100);
    // REC-BUG1 (bit #3707): PAGINAR hasta traer TODOS. Antes solo pedia page=1 y topaba
    // en 100 -- con catalogos >100 insumos, los de la pagina 2+ llegaban SIN config
    // (sub_estados/tamano) y reproducian el bug del conteo para esos insumos. EC tiene
    // 57 hoy, pero era una bomba de tiempo con catalogos mas grandes (lo marco Ares).
    const all = [];
    let page = 1;
    let totalCount = null;
    const MAX_PAGES = 50; // backstop anti-loop (~5000 insumos)
    while (page <= MAX_PAGES) {
      const url = `${BASE_BIZ_INSUMOS}/${encodeURIComponent(businessId)}/insumos?page=${page}&pageSize=${pageSize}`;
      const res = await ApiBase.get(url);
      if (!res.ok) {
        // pagina 1 falla -> el caller cae al fallback legacy; pagina >1 falla ->
        // devolvemos lo ya acumulado (mejor config parcial que ninguna).
        // CONTEO-CATALOGO-CANONICO-T1: el CODIGO viaja al caller. ApiBase traduce el error
        // a una frase en espanol y ahi ya no dice 403, asi que sin res.code el kiosko no
        // puede distinguir "no tienes permiso" de "no hay red" (el 403 del 17-sep).
        if (page === 1) return { ok: false, fallback: true, code: res.code ?? res.details?.status ?? null, error: res.error || 'endpoint no disponible' };
        break;
      }
      // InsumosListResponseDto.Items - puede venir como items o Items segun JsonNamingPolicy
      const payload = res.data?.data || {};
      const items = Array.isArray(payload.items) ? payload.items
                  : Array.isArray(payload.Items) ? payload.Items
                  : Array.isArray(payload) ? payload : [];
      all.push(...items);
      totalCount = payload.totalCount ?? payload.TotalCount ?? totalCount;
      if (items.length < pageSize) break;                       // ultima pagina (parcial)
      if (totalCount !== null && all.length >= totalCount) break; // ya trajimos todo
      page++;
    }
    // T169 pieza A: el total viaja tambien desde el cache, para que la pantalla sepa si la lista se corto.
    _insumosCache.set(businessId, { ts: Date.now(), items: all, totalCount: totalCount ?? all.length });
    return { ok: true, data: all, totalCount: totalCount ?? all.length };
  },

  /**
   * PATCH /api/v1/businesses/{bizId}/insumos/{insumoId}/config
   * Persiste config sub-estados + unidad_conteo + cantidad_por_unidad de un insumo.
   * Mision: PANEL-INVENTARIO-CONFIG-PERSIST-BD-V1-001 T2 (Argy-A T1 backend).
   *
   * @param {string} businessId
   * @param {string|number} insumoId
   * @param {object} config - { sub_estados_config?: {enabled, estados}|null, unidad_conteo?, cantidad_por_unidad? }
   */
  async patchInsumoConfig(businessId, insumoId, config) {
    if (!businessId || insumoId === undefined || insumoId === null) {
      return { ok: false, error: 'businessId + insumoId requeridos' };
    }
    const url = `${BASE_BIZ_INSUMOS}/${encodeURIComponent(businessId)}/insumos/${encodeURIComponent(insumoId)}/config`;
    const res = await ApiBase.patch(url, config || {});
    if (res.ok) {
      // Invalidar cache para forzar re-fetch
      invalidateInsumosCache(businessId);
      // BroadcastChannel notify para sincronia cross-tab
      if (typeof BroadcastChannel !== 'undefined') {
        try {
          const ch = new BroadcastChannel('nx-inv-config');
          ch.postMessage({ type: 'config-changed', bizId: businessId, insumoId, ts: Date.now() });
          ch.close();
        } catch { /* no-op */ }
      }
    }
    return res;
  },


  /**
   * Devuelve los ultimos N conteos de un usuario en un negocio (V1).
   * Filtra en el frontend por nombre de usuario (no hay endpoint dedicado aun).
   *
   * @param {string} businessId
   * @param {string} usuarioNombre - nombre del operador o role
   * @param {number} limit - cuantos conteos devolver (default 2)
   * @returns {Promise<{ok:boolean, data?: Array<{fecha:string, usuario:string, insumos_contados:number, items:Array, resumen:object, rowId:string}>, error?:string}>}
   */
  async getHistorialUsuario(businessId, usuarioNombre, limit = 2) {
    if (!businessId) return { ok: true, data: [] };
    const params = new URLSearchParams({
      businessId,
      page: '1',
      pageSize: '50' // traemos amplio para luego filtrar por usuario en el frontend
    });
    const res = await ApiBase.get(`${BASE_SYNC}?${params}`);
    if (!res.ok) return { ok: false, error: res.error || 'No se pudo cargar historial' };

    const items = res.data?.data?.items || [];
    const sesiones = items
      .map(it => {
        const d = it.data || {};
        return {
          rowId: it.rowId,
          fecha: d.fecha || it.syncedAt || it.updatedAt,
          usuario: d.usuario || null,
          operador_id: d.operador_id || null,
          insumos_totales: d.insumos_totales || 0,
          insumos_contados: d.insumos_contados || (Array.isArray(d.items) ? d.items.length : 0),
          items: d.items || [],
          resumen: d.resumen || null
        };
      })
      .filter(s => !usuarioNombre || (s.usuario && s.usuario.toLowerCase() === String(usuarioNombre).toLowerCase()))
      .sort((a, b) => String(b.fecha).localeCompare(String(a.fecha)))
      .slice(0, limit);

    return { ok: true, data: sesiones };
  },

  /**
   * Devuelve TODAS las verificaciones de un negocio (todos los operadores).
   * FIXES-V1 T3: vista Historial admin.
   *
   * @param {string} businessId
   * @param {object} opts - { from?: 'YYYY-MM-DD', to?: 'YYYY-MM-DD', operador?: string, limit?: number }
   */
  async getHistorialBusiness(businessId, opts = {}) {
    if (!businessId) return { ok: true, data: [] };
    const params = new URLSearchParams({
      businessId,
      page: '1',
      pageSize: String(opts.limit || 100)
    });
    const res = await ApiBase.get(`${BASE_SYNC}?${params}`);
    if (!res.ok) return { ok: false, error: res.error || 'No se pudo cargar historial' };

    const items = res.data?.data?.items || [];
    const fromDate = opts.from ? new Date(opts.from + 'T00:00:00Z') : null;
    const toDate = opts.to ? new Date(opts.to + 'T23:59:59Z') : null;

    const sesiones = items
      .map(it => {
        const d = it.data || {};
        return {
          rowId: it.rowId,
          fecha: d.fecha || it.syncedAt || it.updatedAt,
          usuario: d.usuario || null,
          operador_id: d.operador_id || null,
          operador_cargo: d.operador_cargo || null,
          insumos_totales: d.insumos_totales || 0,
          insumos_contados: d.insumos_contados || (Array.isArray(d.items) ? d.items.length : 0),
          items: d.items || [],
          resumen: d.resumen || null,
          syncedAt: it.syncedAt,
          // CONTEO-HISTORIAL-ESTADOS-T1: 'desktop' = lo confirmado (ajuste aplicado); sin origen = conteo del panel.
          origen: d.origen || null
        };
      })
      .filter(s => {
        if (fromDate && new Date(s.fecha) < fromDate) return false;
        if (toDate && new Date(s.fecha) > toDate) return false;
        if (opts.operador && s.usuario && !s.usuario.toLowerCase().includes(String(opts.operador).toLowerCase())) return false;
        return true;
      })
      .sort((a, b) => String(b.fecha).localeCompare(String(a.fecha)));

    return { ok: true, data: sesiones };
  },

  /**
   * Envia la sesion de conteo al endpoint Desktop V1.5 (cuando Argy-D T5 lo publique).
   * Por ahora hace fallback al endpoint /api/sync/upload existente.
   *
   * @param {object} sesion - payload buildSesionPayload() de InventarioView
   * @param {string} businessId
   * @returns {Promise<{ok:boolean, target:'desktop-v15'|'sync-upload'|'offline', error?:string}>}
   */
  async enviarConteoDesktop(sesion, businessId) {
    // Stub para Desktop V1.5: cuando Argy-D T5 publique endpoint, configurarlo aqui.
    // El endpoint esperado sera algo tipo POST http://desktop-host/api/v1/inventario/conteo
    // controlado por config (AppConfig.desktopBase) o por descubrimiento (probe).
    //
    // V1 actual: usar /api/sync/upload con table='verificacion_inventario'.
    // SEC-SYNC-NEGOCIO-T1 (#15532): nunca al negocio 'default'.
    const negocio = negocioDeLaSubida(businessId);
    if (!negocio) return { ok: false, target: 'sync-upload', error: 'SIN_NEGOCIO' };
    const payload = {
      table: 'verificacion_inventario',
      business_id: negocio,
      rows: [sesion],
      row_count: 1,
      synced_at: new Date().toISOString()
    };
    const res = await ApiBase.post('/api/sync/upload', payload);
    return res.ok
      ? { ok: true, target: 'sync-upload' }
      : { ok: false, target: 'sync-upload', error: res.error || 'POST /api/sync/upload fallo' };
  },

  /**
   * GET /api/v1/inventario/grupos/estado/resumen?businessId=biz_X&fecha=YYYY-MM-DD
   *
   * PANEL-OPERATIONS-001 (ASSIGN #2005) -- avance del conteo del dia por grupo.
   * Contrato LOCKEADO por Argy-A en bitacora #2011 (fields_locked=true):
   *   data: { businessId, fecha, grupos: [{ grupoId, grupoNombre, totalItems,
   *           contados, faltantes, avancePct, diferenciaTotal, sesiones,
   *           ultimaSesionAt }], totales: { totalItems, contados, faltantes, avancePct } }
   *
   * Semantica #2011: fecha = dia OPERATIVO Colombia (America/Bogota), default hoy
   * SERVER-SIDE si se omite (el cliente NO calcula timezone); grupos sin sesion
   * vienen con contados=0; "contados" = insumos DISTINTOS del grupo en sesiones
   * del dia (gana la ultima aparicion).
   *
   * NOT_IMPLEMENTED si 404 (nexo-api PR #87 sin deployar): la vista degrada con
   * banner y se enciende sola al salir el deploy (patron B-T3).
   *
   * @param {string} businessId
   * @param {{ fecha?: string }} opts - ISO date opcional; omitir = hoy Bogota
   */
  async getGruposEstadoResumen(businessId, { fecha } = {}) {
    if (!businessId) return { ok: false, error: { code: 'BUSINESS_REQUIRED' } };
    const qs = new URLSearchParams({ businessId });
    if (fecha) qs.set('fecha', fecha);
    const res = await ApiBase.get(`/api/v1/inventario/grupos/estado/resumen?${qs}`);
    if (!res.ok) {
      if (/not\s*found|404/i.test(JSON.stringify(res.error || ''))) {
        return { ok: false, error: { code: 'NOT_IMPLEMENTED' } };
      }
      return res;
    }
    const d = res.data?.data ?? res.data ?? {};
    return {
      ok: true,
      data: {
        businessId: d.businessId ?? businessId,
        fecha: d.fecha ?? fecha ?? null,
        grupos: Array.isArray(d.grupos) ? d.grupos : [],
        totales: d.totales ?? { totalItems: 0, contados: 0, faltantes: 0, avancePct: 0 }
      }
    };
  }
};
